import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SearchSalesGrossProfit from '../PiechartSalesFinance/SearchSalesGrossProfit'
import SearchedSalesNetMargin from '../PiechartSalesFinance/SearchedSalesNetMargin'
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchLeads } from '../../../../../store/slices/leadsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGroups } from '../../../../../store/slices/groupSlice';
import { ValidDate } from "../../../../../utils/DateUtils";
import { fetchAllProjects } from "../../../../../store/slices/projectsSlice";
import { fetchEmployees } from "../../../../../store/slices/employeeAdminSlice";
import { fetchWorkEntries, fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import { fetchHardwareDescriptions, fetchHardwareProjectTracking } from "../../../../../store/slices/hardwareDescriptionSlice";
import { fetchDescriptionTasks, fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import { fetchSalesHourlyRates } from "../../../../../store/slices/salesHourlyRateSlice";
import { fetchCompanyProfiles } from "../../../../../store/slices/companyProfileSlice";
import { hasViewOrFullAccess } from "../../../../../utils/featureAccessUtils";
import { fetchSalesTaxes } from "../../../../../store/slices/salesTaxesSlice";

const FinanceGraph = ({
  currentEmployee,
  perdiem,
  averagecost,
  erpPlQuotedTotalsForGrossProfitCard,
  erpPlValueSumForCard,
  erpPlCostSumForCard,
  erpPlGrossProfitSumForCard,
  erpPlShowQuotedExpenseForRow,
  /** Optional: when embedded under P&L, use the same lead universe as P&L (independent filters). */
  plLeadsUniverse,
  /** Optional (CRM only): value/expense accessors matching P&L table columns. */
  plGetQuotedValueForRow,
  plGetQuotedExpenseForRow,
}) => {
  const dispatch = useDispatch();
  const [GroupNamesL3, setGroupNamesL3] = useState([]);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [yearData, setYearData] = useState([]);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [AssignedGroup, setAssignedGroup] = useState("All");
  const [clientFilter, setClientFilter] = useState();
  // const [showAllLeadTable, setShowAllLeadTable] = useState(true);
  // const [calculationTable, setCalculationTable] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [leadData, setLeadData] = useState([]);
  const { leads, allLeads } = useSelector((state) => state.leads);
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState(["2026"]);
  const [graphViewMode, setGraphViewMode] = useState("QuotedExpense");
  const [filterVersion, setFilterVersion] = useState(0);
  const [expandedYear, setExpandedYear] = useState(null);
  const [searchedLeadReportswithoutyears, setSearchedLeadReportswithoutyears] = useState([]);
  const [loggedUserData, setLoggedUserData] = useState([]);
  const [l3Leads, setL3Leads] = useState([]);
  const { group } = useSelector((state) => state.group);
  const { projects } = useSelector((state) => state.projects);
  const { employees } = useSelector((state) => state.employees);
  const { entries, projectTrackingWorkEntries } = useSelector((state) => state.workentries);
  const { items, hardwareProjectTracking } = useSelector((state) => state.hardwareDescription);
  const { tasks, descriptionProjectTracking } = useSelector((state) => state.descriptionTasks);
  const { data: salesHourlyRates } = useSelector((state) => state.salesHourlyRate);
  const { profiles } = useSelector((state) => state.companyProfile);

  const gpPkgNormalized = String(profiles?.[0]?.subscriptionpackage || "").replace(/\s+/g, " ").trim().toLowerCase();
  const gpIsCrmPackage = gpPkgNormalized === "crm" || gpPkgNormalized.includes("crm") || (gpPkgNormalized.length > 0 && !gpPkgNormalized.includes("erp")) || (gpPkgNormalized.length === 0);
  const gpIsFinanceWithPL = hasViewOrFullAccess(currentEmployee, "PROFIT AND LOSS");
  const gpHasGrossProfitModuleAccess = hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER");

  const selectedYearForTotal = (() => {
    const period = selectedYears?.[0];
    if (!period) return null;
    const m = String(period).trim().match(/^(\d{4})/);
    return m ? Number(m[1]) : null;
  })();

  const packages = profiles?.[0]?.subscriptionpackage;
  const pkgNormalizedGraph = String(packages || "").replace(/\s+/g, " ").trim().toLowerCase();
  const isCrmPackage = pkgNormalizedGraph === "crm" || pkgNormalizedGraph.includes("crm") || (pkgNormalizedGraph.length > 0 && !pkgNormalizedGraph.includes("erp")) || (pkgNormalizedGraph.length === 0);

  // --- Group helpers (store groupid in filters; show name from group slice) ---
  const groupNameById = useMemo(() => {
    const map = new Map();
    const normalize = (v) => String(v ?? "").trim();
    (Array.isArray(group) ? group : []).forEach((g) => {
      const id = normalize(g?._id ?? g?.id);
      if (!id) return;
      const name = normalize(
        g?.name ??
        g?.newGroupName ??
        g?.groupName ??
        g?.GroupName ??
        id
      );
      map.set(id, name || id);
    });
    return map;
  }, [group]);

  const groupIdByName = useMemo(() => {
    const map = new Map();
    groupNameById.forEach((name, id) => map.set(String(name).trim(), id));
    return map;
  }, [groupNameById]);

  const getLeadGroupId = useCallback((lead) => {
    const normalize = (value) => String(value ?? "").trim();
    const g = lead?.groupid ?? lead?.groupId;
    const fromObject = normalize(g?._id ?? g?.id ?? g?.$oid);
    if (fromObject) return fromObject;
    const fromString =
      typeof lead?.groupid === "string" ? normalize(lead.groupid) : "";
    const fromStringAlt =
      typeof lead?.groupId === "string" ? normalize(lead.groupId) : "";
    const sid = fromString || fromStringAlt;
    if (sid && groupNameById.has(sid)) return sid;
    if (sid && groupIdByName.has(sid)) return groupIdByName.get(sid);
    const fromAssigned = normalize(lead?.AssignedGroup);
    if (fromAssigned && groupIdByName.has(fromAssigned)) return groupIdByName.get(fromAssigned);
    return "";
  }, [groupNameById, groupIdByName]);

  const getProjectGroupIdForLeadRow = useCallback((leadRow) => {
    if (!Array.isArray(projects) || projects.length === 0) return "";
    const key =
      leadRow?.quotationId != null && String(leadRow.quotationId).trim() !== ""
        ? String(leadRow.quotationId).trim()
        : (leadRow?.ProjectName ?? "");

    const proj = projects.find((p) => {
      if (p?.quotationId != null && String(p.quotationId).trim() !== "") {
        return String(p.quotationId).trim() === String(key).trim();
      }
      return (
        String(p?.ProjectName || "").trim().toLowerCase() ===
        String(leadRow?.ProjectName || "").trim().toLowerCase()
      );
    });
    if (!proj) return "";
    const g = proj.groupid ?? proj.groupId;
    const gid = g?._id ?? g?.id ?? (typeof g === "string" ? g : "");
    return String(gid || "").trim();
  }, [projects]);

  const getRowGroupId = useCallback((row) => {
    // CRM package: group comes from lead.groupid
    if (isCrmPackage) return getLeadGroupId(row);
    // ERP package: group comes from projectdata.groupid
    const pid = getProjectGroupIdForLeadRow(row);
    return pid || getLeadGroupId(row);
  }, [isCrmPackage, getLeadGroupId, getProjectGroupIdForLeadRow]);

  // const getLeadDateObj = (lead) => {
  //   const raw = lead?.LeadDate;
  //   const normalized = ValidDate(raw);
  //   if (normalized) {
  //     const [y, m, d] = normalized.split("-").map((v) => Number(v));
  //     const dateObj = new Date(y, m - 1, d); // local date (avoids timezone shifting YYYY-MM-DD)
  //     return Number.isNaN(dateObj.getTime()) ? null : dateObj;
  //   }
  //   const dateObj = new Date(raw);
  //   return Number.isNaN(dateObj.getTime()) ? null : dateObj;
  // };

  const filterVersionMountRef = useRef(true);
  useEffect(() => {
    if (filterVersionMountRef.current) {
      filterVersionMountRef.current = false;
      return;
    }
    setFilterVersion((v) => v + 1);
  }, [selectedYears]);

  const resetDependentFiltersForGroup = () => {
    setClientFilter("All");
    setSelectedYears([currentYear.toString()]);
    setExpandedYear(null);
    setShowClientDropdown(false);
    setIsYearOpen(false);
  };

  useEffect(() => {
    dispatch(fetchLeads())
    dispatch(fetchGroups());
    // Finance graphs should not depend on SalesFinaceFilter’s computed arrays.
    // Fetch required base data here (idempotent thunks are OK).
    dispatch(fetchAllProjects());
    dispatch(fetchEmployees());
    dispatch(fetchWorkEntries());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchDescriptionTasks());
    dispatch(fetchSalesHourlyRates());
    dispatch(fetchCompanyProfiles());
    dispatch(fetchWorkEntriesProjectTracking());
    dispatch(fetchDescriptionTasksProjectTracking());
    dispatch(fetchHardwareProjectTracking());
    dispatch(fetchSalesTaxes());
  }, [dispatch]);

  useEffect(() => {
    if (l3Leads) {
      setLoggedUserData([...l3Leads].reverse());
    }
  }, [l3Leads]);
  useEffect(() => {
    if (Array.isArray(plLeadsUniverse)) {
      setL3Leads(plLeadsUniverse);
      if (group) setGroupNamesL3(group);
      return;
    }
    const useAllLeads = isCrmPackage || gpIsFinanceWithPL || gpHasGrossProfitModuleAccess;
    const source = useAllLeads
      ? (allLeads || [])
      : ((!leads || leads.length === 0) ? (allLeads || []) : (leads || []));
    setL3Leads(source);
    if (group) {
      setGroupNamesL3(group);
    }
  }, [plLeadsUniverse, leads, allLeads, group, isCrmPackage, gpIsFinanceWithPL, gpHasGrossProfitModuleAccess]);

  useEffect(() => {
    if (loggedUserData.length === 0) return;

    let filtered = loggedUserData;
    // Filter by groupid (skip if "All")
    if (AssignedGroup && AssignedGroup !== "All") {
      filtered = filtered.filter(
        (lead) => getRowGroupId(lead) === AssignedGroup
      );
    }

    // Filter by ClientName (skip if "All")
    if (clientFilter && clientFilter !== "All") {
      filtered = filtered.filter((lead) => lead.ClientName === clientFilter);
    }

    setSearchedLeadReportswithoutyears(filtered);
  }, [loggedUserData, AssignedGroup, clientFilter, getRowGroupId]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const allMonths = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const quarters = ["Jan - Mar", "Apr - Jun", "Jul - Sep", "Oct - Dec"];

    const data = Array.from({ length: 5 }, (_, i) => {
      const year = currentYear - i;
      return {
        Year: year,
        months: allMonths,
        quarter: quarters,
      };
    });

    setYearData(data);
  }, []);

  useEffect(() => {
    if (Array.isArray(plLeadsUniverse)) {
      setLeadData(plLeadsUniverse);
      return;
    }
    const useAll = gpIsCrmPackage || gpIsFinanceWithPL || gpHasGrossProfitModuleAccess;
    const source = useAll
      ? (allLeads || [])
      : ((!leads || leads.length === 0) ? (allLeads || []) : (leads || []));
    setLeadData(source);
  }, [plLeadsUniverse, leads, allLeads, gpIsCrmPackage, gpIsFinanceWithPL, gpHasGrossProfitModuleAccess]);

  const gpFinanceDataProject = useMemo(() => {
    if (!Array.isArray(projects) || projects.length === 0) return [];
    return projects
      .filter((proj) => proj.isProjecttracking === false)
      .filter((project) => {
        if (!project?.Assignedperson?.length) return false;
        return project.Assignedperson.some(
          (person) =>
            (person.salespersonaction === "accept" ||
              person.salespersonaction === "project not-assigned") &&
            (person.salespersonstatus === "response received" ||
              person.salespersonstatus === "project approved" ||
              person.salespersonstatus === "project not-approved" ||
              person.salespersonstatus === "project not-assigned")
        );
      });
  }, [projects]);

  const gpQuotedProject = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects.filter(
      (proj) => proj.isProjecttracking === false && proj.isProjectQuoted === true
    );
  }, [projects]);

  const gpActualFinanceDataProject = useMemo(() => {
    if (!Array.isArray(projects) || projects.length === 0) return [];
    return projects
      .filter((proj) => proj.isProjecttracking === true)
      .filter((project) => {
        if (!project?.Assignedperson?.length) return false;
        return project.Assignedperson.every(
          (person) =>
            person.salespersonstatus === "project ongoing" ||
            person.salespersonstatus === "project complete"
        );
      });
  }, [projects]);

  const gpActualQuotedProject = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects.filter(
      (proj) => proj.isProjecttracking === true && proj.isProjectQuoted === true
    );
  }, [projects]);

  const gpSearchedCalcProjects = useMemo(() => {
    const allLeads = searchedLeadReportswithoutyears || [];
    if (!gpFinanceDataProject?.length || !allLeads.length) return [];
    return gpFinanceDataProject.filter((row) =>
      allLeads.some((lead) =>
        row.quotationId != null && lead.quotationId != null
          ? String(row.quotationId).trim() === String(lead.quotationId).trim()
          : row.ProjectName === lead.ProjectName
      )
    );
  }, [gpFinanceDataProject, searchedLeadReportswithoutyears]);

  const gpMergedProjects = useMemo(() => {
    if (!gpSearchedCalcProjects?.length) return [];
    const entriesArr = Array.isArray(entries) ? entries : [];
    const tasksArr = Array.isArray(tasks) ? tasks : [];
    const itemsArr = Array.isArray(items) ? items : [];
    return gpSearchedCalcProjects.map((proj) => {
      const projectId = proj?._id;
      return {
        ...proj,
        TaskList: entriesArr.filter((e) => e.projectid === projectId),
        Description: tasksArr.filter((t) => t.projectid === projectId),
        HardwareList: itemsArr.filter((h) => h.projectid === projectId),
      };
    });
  }, [gpSearchedCalcProjects, entries, tasks, items]);

  const gpCombinedProjects = useMemo(() => {
    if (!gpMergedProjects?.length) return [];
    const groups = {};
    gpMergedProjects.forEach((proj) => {
      const key =
        proj.quotationId != null && String(proj.quotationId).trim() !== ""
          ? String(proj.quotationId).trim()
          : proj.ProjectName;
      if (!groups[key]) {
        groups[key] = {
          ProjectName: proj.ProjectName,
          quotationId: proj.quotationId ?? null,
          salespersonEmail: proj.SalesPersonEmail,
          TaskList: [],
          Description: [],
          HardwareList: [],
        };
      }
      groups[key].TaskList.push(...(proj.TaskList || []));
      groups[key].Description.push(...(proj.Description || []));
      groups[key].HardwareList.push(...(proj.HardwareList || []));
    });
    return Object.values(groups);
  }, [gpMergedProjects]);

  const gpHourlyRateData = useMemo(() => {
    if (!salesHourlyRates || !currentEmployee?._id) return [];
    return salesHourlyRates.filter((q) => q.createdBy === currentEmployee._id);
  }, [salesHourlyRates, currentEmployee]);

  const quotationProjects = useMemo(() => {
    if (!gpCombinedProjects?.length || !employees?.length) return [];

    return gpCombinedProjects.map((project) => {
      const assignedEmployee = employees.find(
        (emp) => emp.EmpEmail === project.salespersonEmail
      );
      if (!assignedEmployee) return project;

      const enrichWithDesignationAndRates = (arr = []) =>
        arr.map((item) => {
          const emp =
            employees.find((e) => e._id === item.taskownerid) ||
            employees.find((e) => e._id === item.createdBy);

          let designation = item.designation || null;
          if (!designation && emp) {
            const parts = [];
            if (emp.servicetype) parts.push(emp.servicetype);
            if (emp.customRoles?.length) parts.push(emp.customRoles[0]);
            designation = parts.join(" ").trim() || null;
          }

          if (!designation) {
            return {
              ...item,
              designation: null,
              hourlysalaray: item.hourlysalaray || 0,
              overheadpercentage: item.overheadpercentage || 0,
            };
          }

          const matchedQuote = gpHourlyRateData?.find(
            (q) => q.designation === designation
          );

          return {
            ...item,
            designation,
            hourlysalaray:
              (item.hourlysalaray ?? (matchedQuote?.hourlysalaray ?? 0)) || 0,
            overheadpercentage:
              (item.overheadpercentage ??
                (matchedQuote?.overheadpercentage ?? 0)) || 0,
          };
        });

      return {
        ...project,
        TaskList: enrichWithDesignationAndRates(project.TaskList),
        Description: enrichWithDesignationAndRates(project.Description),
        HardwareList: enrichWithDesignationAndRates(project.HardwareList),
      };
    });
  }, [gpCombinedProjects, employees, gpHourlyRateData]);

  const calculatedProjects = useMemo(() => {
    if (!quotationProjects || quotationProjects.length === 0) return [];

    return quotationProjects.map((proj) => {
      let taskValue = 0;
      let descriptionValue = 0;
      let hardwareValue = 0;

      if (proj.TaskList?.length > 0) {
        const projectKey =
          proj.quotationId != null && String(proj.quotationId).trim() !== ""
            ? String(proj.quotationId).trim()
            : proj.ProjectName;
        const projectExistsInQuoted = gpQuotedProject.some(
          (qp) =>
            (qp.quotationId != null &&
              String(qp.quotationId).trim() !== "" &&
              String(qp.quotationId) === String(projectKey)) ||
            (qp.ProjectName === projectKey &&
              (qp.quotationId == null ||
                String(qp.quotationId).trim() === ""))
        );

        taskValue = proj.TaskList.reduce((total, task) => {
          const hoursSpent = Number(task.hoursassigned) || 0;
          let hourlyRate = 0;

          if (projectExistsInQuoted) {
            const taskQuotedProject = gpQuotedProject.find(
              (qp) => qp._id === task.projectid
            );
            if (taskQuotedProject) {
              const isManager = task.designation
                ?.toLowerCase()
                .includes("manager");
              const isOperations = task.designation
                ?.toLowerCase()
                .includes("operations");
              if (isManager)
                hourlyRate = taskQuotedProject.managerHourlyRate || 0;
              else if (isOperations)
                hourlyRate = taskQuotedProject.operationHourlyRate || 0;
            }
            if (hourlyRate === 0) {
              hourlyRate =
                (Number(task.hourlysalaray) || 0) +
                (Number(task.overheadpercentage) || 0);
            }
          } else {
            hourlyRate =
              (Number(task.hourlysalaray) || 0) +
              (Number(task.overheadpercentage) || 0);
          }

          return total + hoursSpent * hourlyRate;
        }, 0);
      }

      if (proj.Description?.length > 0) {
        const installationTripByOwner = {};
        proj.Description.forEach((desc) => {
          if (
            desc.typeservice &&
            desc.typeservice.toLowerCase().includes("installation") &&
            desc.taskownerid &&
            desc.tripduration
          ) {
            if (!installationTripByOwner[desc.taskownerid]) {
              installationTripByOwner[desc.taskownerid] = desc.tripduration;
            } else {
              installationTripByOwner[desc.taskownerid] = Math.max(
                installationTripByOwner[desc.taskownerid],
                desc.tripduration
              );
            }
          }
          if (desc.value) {
            descriptionValue += desc.value;
          }
        });
        Object.values(installationTripByOwner).forEach((tripDuration) => {
          descriptionValue +=
            tripDuration * perdiem + tripDuration * averagecost;
        });
      }

      if (proj.HardwareList?.length > 0) {
        const hardwareByDescription = {};
        proj.HardwareList.forEach((hw) => {
          const description = hw.assemblepartdescription;
          if (!hardwareByDescription[description]) {
            hardwareByDescription[description] = hw;
          } else {
            if (hw.totalprice > hardwareByDescription[description].totalprice) {
              hardwareByDescription[description] = hw;
            }
          }
        });
        Object.values(hardwareByDescription).forEach((hw) => {
          if (hw.totalprice) hardwareValue += hw.totalprice;
        });
      }

      return {
        ProjectName: proj.ProjectName,
        quotationId: proj.quotationId ?? null,
        TaskListValue: taskValue,
        DescriptionValue: descriptionValue,
        HardwareListValue: hardwareValue,
      };
    });
  }, [quotationProjects, perdiem, averagecost, gpQuotedProject]);

  const directActualCostMap = useMemo(() => {
    const costMap = {};
    if (!gpActualFinanceDataProject?.length) return costMap;

    const ptEntries = Array.isArray(projectTrackingWorkEntries) ? projectTrackingWorkEntries : [];
    const ptDescs = Array.isArray(descriptionProjectTracking) ? descriptionProjectTracking : [];
    const ptHw = Array.isArray(hardwareProjectTracking) ? hardwareProjectTracking : [];

    const projRowKey = (proj) =>
      proj.quotationId != null && String(proj.quotationId).trim() !== ""
        ? String(proj.quotationId).trim()
        : proj.ProjectName;

    const groups = {};
    gpActualFinanceDataProject.forEach((proj) => {
      const key = projRowKey(proj);
      if (!groups[key]) {
        groups[key] = {
          projectIds: [],
          ProjectName: proj.ProjectName,
          quotationId: proj.quotationId,
          salespersonEmail: proj.SalesPersonEmail,
        };
      }
      groups[key].projectIds.push(proj._id);
    });

    // Match P&L Actual Expenses cache (written by ActualFinaceTable/ActualAllServiceTable).
    const ACTUAL_PL_COST_CACHE_KEY = "actual_pl_cost_per_service_by_rowKey_v1";
    let cachedActualCostByRowKey = {};
    try {
      const raw =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(ACTUAL_PL_COST_CACHE_KEY)
          : null;
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") cachedActualCostByRowKey = parsed;
    } catch (e) {
      cachedActualCostByRowKey = {};
    }

    Object.entries(groups).forEach(([key, group]) => {
      // 1) If P&L has already computed and cached the itemized total, use it (source of truth).
      const cached = cachedActualCostByRowKey?.[String(key).trim()];
      if (cached !== undefined && cached !== null && Number.isFinite(Number(cached))) {
        costMap[key] = Math.round(Number(cached) || 0);
        return;
      }

      // 2) Otherwise compute using the same approach as `ActualFinaceTable.showExpensesop` fallback.
      const pIds = group.projectIds;
      const tasks = ptEntries.filter((e) => pIds.includes(e.projectid));
      const descs = ptDescs.filter((t) => pIds.includes(t.projectid));
      const hw = ptHw.filter((h) => pIds.includes(h.projectid));

      const projectExistsInQuoted = gpActualQuotedProject?.some((qp) => projRowKey(qp) === key);

      const totalTaskValue = tasks.reduce((total, task) => {
        const hours = Number(task.hoursassigned ?? task.hoursspent) || 0;

        // Build designation (same as ActualFinaceTable)
        const emp =
          employees?.find((e) => e._id === task.taskownerid) ||
          employees?.find((e) => e._id === task.createdBy);
        const designationParts = [];
        if (emp?.servicetype) designationParts.push(emp.servicetype);
        if (emp?.customRoles?.length) designationParts.push(emp.customRoles[0]);
        const designation = designationParts.join(" ").trim().toLowerCase();

        let hourlyRate = 0;
        if (projectExistsInQuoted) {
          const taskQuotedProject = gpActualQuotedProject?.find((qp) => qp._id === task.projectid);
          if (taskQuotedProject) {
            const isManager = designation.includes("manager");
            const isOperations = designation.includes("operations");
            if (isManager) hourlyRate = Number(taskQuotedProject.managerHourlyRate) || 0;
            else if (isOperations) hourlyRate = Number(taskQuotedProject.operationHourlyRate) || 0;
          }
        }

        if (hourlyRate === 0) {
          // Match Itemized P&L: hourly salary only (no overhead addition)
          hourlyRate = Number(task.hourlysalaray) || 0;
        }

        return total + hours * hourlyRate;
      }, 0);

      // Associated cost: description expenses/value + installation trip costs
      let descriptionValue = 0;
      const installationTripByOwner = {};
      descs.forEach((desc) => {
        if (
          desc.typeservice &&
          String(desc.typeservice).toLowerCase().includes("installation") &&
          desc.taskownerid &&
          desc.tripduration
        ) {
          const owner = String(desc.taskownerid);
          const td = Number(desc.tripduration) || 0;
          installationTripByOwner[owner] = Math.max(installationTripByOwner[owner] || 0, td);
        }
        const exp = Number(desc.expenses ?? desc.value ?? 0) || 0;
        if (exp) descriptionValue += exp;
      });
      Object.values(installationTripByOwner).forEach((td) => {
        descriptionValue += (Number(td) || 0) * (Number(perdiem) || 0) + (Number(td) || 0) * (Number(averagecost) || 0);
      });

      // Hardware: highest totalprice per assemblepartdescription
      let hardwareValue = 0;
      const hardwareByDescription = {};
      hw.forEach((hwi) => {
        const d = hwi.assemblepartdescription;
        if (!d) return;
        if (!hardwareByDescription[d]) hardwareByDescription[d] = hwi;
        else if (Number(hwi.totalprice) > Number(hardwareByDescription[d].totalprice)) {
          hardwareByDescription[d] = hwi;
        }
      });
      Object.values(hardwareByDescription).forEach((hwi) => {
        hardwareValue += Number(hwi.totalprice) || 0;
      });

      costMap[key] = Math.round(Number(totalTaskValue + descriptionValue + hardwareValue) || 0);
    });

    console.log("[GP DEBUG] directActualCostMap:", JSON.parse(JSON.stringify(costMap)));
    console.log("[GP DEBUG] gpActualFinanceDataProject count:", gpActualFinanceDataProject.length);
    console.log("[GP DEBUG] tracking entries count:", ptEntries.length, "descs:", ptDescs.length, "hw:", ptHw.length);

    return costMap;
  }, [
    gpActualFinanceDataProject,
    projectTrackingWorkEntries,
    descriptionProjectTracking,
    hardwareProjectTracking,
    gpActualQuotedProject,
    employees,
    perdiem,
    averagecost,
  ]);

  const getActualProjectCost = useCallback((projectKey) => {
    return directActualCostMap[projectKey] || 0;
  }, [directActualCostMap]);

  const dropdownRefGroup = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefGroup.current &&
        !dropdownRefGroup.current.contains(event.target)
      ) {
        setIsGroupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const dropdownRefYear = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefYear.current &&
        !dropdownRefYear.current.contains(event.target)
      ) {
        setIsYearOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const dropdownRefclient = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefclient.current &&
        !dropdownRefclient.current.contains(event.target)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClientChange = (clientValue) => {
    setClientFilter(clientValue);
    setShowClientDropdown(false);
  };

  const uniqueClients = [
    ...new Map(
      leadData
        .filter((lead) =>
          AssignedGroup === "All"
            ? true                          // ✅ show all clients
            : getRowGroupId(lead) === AssignedGroup
        )
        .map((lead) => [
          lead.ClientName?.trim().toLowerCase(), // key for uniqueness
          lead.ClientName?.trim(),               // display value
        ])
    ).values(),
  ].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));

  const normalizeYearSelection = (value) => String(value ?? "").trim();

  const isSelectionInYear = (selection, year) =>
    normalizeYearSelection(selection).startsWith(String(year));

  const setYearOnlySelection = (year) => {
    const yearStr = String(year);
    setSelectedYears((prev) => {
      const current = prev?.length ? normalizeYearSelection(prev[0]) : "";
      // toggle off only if the current selection is exactly this year (not month/quarter)
      if (current === yearStr) return [];
      return [yearStr];
    });
  };

  const handleYearSelection = (value) => {
    const normalized = normalizeYearSelection(value);
    setSelectedYears((prev) => {
      const current = prev?.length ? normalizeYearSelection(prev[0]) : "";
      // Deselecting current month/quarter: keep year selected instead of clearing
      if (current === normalized) {
        const yearMatch = normalized.match(/^(\d{4})/);
        return yearMatch ? [yearMatch[1]] : [];
      }
      return [normalized];
    });
  };


  const canSeeQuotedTab = (() => {
    if (currentEmployee?.EmpAuthLevel === "L1") {
      if (!Array.isArray(currentEmployee?.featureAccess) || currentEmployee.featureAccess.length === 0) return false;
      return hasViewOrFullAccess(currentEmployee, "PROFIT AND LOSS TRACKER QUOTED");
    }
    return true;
  })();

  useEffect(() => {
    if (currentEmployee?.EmpAuthLevel === "L1" && !canSeeQuotedTab) {
      setGraphViewMode("Actual");
    }
  }, [currentEmployee, canSeeQuotedTab]);

  const canSeeActualTab = (() => {
    if (!currentEmployee) return true;
    if (!Array.isArray(currentEmployee?.featureAccess) || currentEmployee.featureAccess.length === 0) return true;
    if (isCrmPackage) return false;
    if (currentEmployee?.EmpAuthLevel === "L1") return true;
    return hasViewOrFullAccess(currentEmployee, "PROFIT AND LOSS TRACKER ACTUAL");
  })();
  const canViewFilter =
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER TOTAL GROSS PROFIT GRAPH") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER TOTAL GROSS PROFIT TABLE") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER YTD GROSS PROFIT GRAPH") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER YTD GROSS PROFIT TABLE") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER NET MARGIN GRAPH") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER NET MARGIN TABLE") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER YTD NET MARGIN GRAPH") ||
    hasViewOrFullAccess(currentEmployee, "GROSS PROFIT TRACKER YTD NET MARGIN TABLE");

  const hasNoGrossProfitFeatureConfig =
    !Array.isArray(currentEmployee?.featureAccess) ||
    currentEmployee.featureAccess.length === 0;
  const isAdminL0 = currentEmployee?.EmpAuthLevel === "L0";
  const canAccessGrossProfitFeature = (featureKey) =>
    hasNoGrossProfitFeatureConfig ||
    isAdminL0 ||
    hasViewOrFullAccess(currentEmployee, featureKey);

  const canViewTotalGrossProfitGraph = canAccessGrossProfitFeature("GROSS PROFIT TRACKER TOTAL GROSS PROFIT GRAPH");
  const canViewTotalGrossProfitTable = canAccessGrossProfitFeature("GROSS PROFIT TRACKER TOTAL GROSS PROFIT TABLE");
  const canViewYtdGrossProfitGraph = canAccessGrossProfitFeature("GROSS PROFIT TRACKER YTD GROSS PROFIT GRAPH");
  const canViewYtdGrossProfitTable = canAccessGrossProfitFeature("GROSS PROFIT TRACKER YTD GROSS PROFIT TABLE");
  const canViewNetMarginGraph = canAccessGrossProfitFeature("GROSS PROFIT TRACKER NET MARGIN GRAPH");
  const canViewNetMarginTable = canAccessGrossProfitFeature("GROSS PROFIT TRACKER NET MARGIN TABLE");
  const canViewYtdNetMarginGraph = canAccessGrossProfitFeature("GROSS PROFIT TRACKER YTD NET MARGIN GRAPH");
  const canViewYtdNetMarginTable = canAccessGrossProfitFeature("GROSS PROFIT TRACKER YTD NET MARGIN TABLE");

  const canViewGrossProfitCards =
    canViewTotalGrossProfitGraph ||
    canViewTotalGrossProfitTable ||
    canViewYtdGrossProfitGraph ||
    canViewYtdGrossProfitTable;

  const canViewNetMarginCards =
    canViewNetMarginGraph ||
    canViewNetMarginTable ||
    canViewYtdNetMarginGraph ||
    canViewYtdNetMarginTable;

  // Gross Profit module uses only its own filter state (independent from P&L module).
  const grossProfitLeadReportsWithoutYears = searchedLeadReportswithoutyears;

  // Local getRowKey for this module (same logic as P&L, but no shared state).
  const getRowKey = useCallback((row) =>
    row?.quotationId != null && String(row.quotationId).trim() !== ""
      ? String(row.quotationId).trim()
      : row?.ProjectName ?? "",
    []
  );

  // Local getProjectCost for this module: use calculatedProjects so totals match this module's view.
  const getProjectCost = useCallback((projectKey) => {
    const p = calculatedProjects.find(
      (proj) =>
        (proj.quotationId != null && String(proj.quotationId) === String(projectKey)) ||
        proj.ProjectName === projectKey
    );
    if (!p) return 0;
    return (p.TaskListValue || 0) + (p.DescriptionValue || 0) + (p.HardwareListValue || 0);
  }, [calculatedProjects]);

  /**
   * Same YTD Value / Cost / GP as SearchSalesGrossProfit (Quoted, non-CRM): used only so Net Margin
   * cards show the same gross-profit base as the YTD Gross Profit card — no change to net-margin formulas.
   */
  const erpYtdTotalsForNetMargin = useMemo(() => {
    if (graphViewMode === "Actual") return null;
    if (typeof erpPlShowQuotedExpenseForRow !== "function") return null;

    // Current year (year-only): YTD should match the same totals used by the Gross Profit card,
    // so Net Margin and YTD Net Margin remain consistent.
    const selectedPeriodForPandlYtd = String(selectedYears?.[0] ?? "").trim();
    const yearOnlyMatchForPandlYtd = selectedPeriodForPandlYtd.match(/^(\d{4})$/);
    const selectedPandlYearForYtd = yearOnlyMatchForPandlYtd ? Number(yearOnlyMatchForPandlYtd[1]) : null;
    const isCurrentYearOnly =
      selectedPandlYearForYtd != null && selectedPandlYearForYtd === new Date().getFullYear();
    const hasPandlTotals =
      erpPlValueSumForCard != null &&
      Number.isFinite(Number(erpPlValueSumForCard)) &&
      erpPlCostSumForCard != null &&
      Number.isFinite(Number(erpPlCostSumForCard)) &&
      erpPlGrossProfitSumForCard != null &&
      Number.isFinite(Number(erpPlGrossProfitSumForCard));

    if (isCurrentYearOnly && hasPandlTotals) {
      const v = Number(erpPlValueSumForCard);
      const c = Number(erpPlCostSumForCard);
      const gp = Number(erpPlGrossProfitSumForCard);
      return { quotedValue: v, productCost: c, grossProfit: gp };
    }

    const getMonthIndex = (monthName) => {
      const months = {
        jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4,
        jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, september: 8, oct: 9, october: 9,
        nov: 10, november: 10, dec: 11, december: 11,
      };
      return months[monthName.toLowerCase()] !== undefined ? months[monthName.toLowerCase()] : -1;
    };

    let baseLeads = grossProfitLeadReportsWithoutYears || [];
    const activeProjects = quotationProjects || [];
    if (!isCrmPackage && activeProjects.length > 0) {
      const validProjectIds = new Set(activeProjects.map((p) => String(p.quotationId || "").trim()));
      const validProjectNames = new Set(activeProjects.map((p) => p.ProjectName));
      baseLeads = baseLeads.filter((lead) => {
        const qId = String(lead.quotationId || "").trim();
        if (lead.quotationId) return validProjectIds.has(qId);
        return validProjectNames.has(lead.ProjectName);
      });
    }

    if (!selectedYears || selectedYears.length === 0) return null;

    const selectedPeriod = String(selectedYears?.[0] ?? "").trim();
    const yearOnlyMatch = selectedPeriod.match(/^(\d{4})$/);
    const monthMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)$/);
    const quarterMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)\s*-\s*([A-Za-z]+)$/);

    const selectedYear =
      yearOnlyMatch?.[1] ? Number(yearOnlyMatch[1]) :
        monthMatch?.[1] ? Number(monthMatch[1]) :
          quarterMatch?.[1] ? Number(quarterMatch[1]) :
            null;

    if (!selectedYear) return null;

    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    let startDate = new Date(selectedYear, 0, 1);
    let endDate;

    if (monthMatch) {
      const mIdx = getMonthIndex(monthMatch[2]);
      if (mIdx !== -1) {
        startDate = new Date(selectedYear, mIdx, 1);
        endDate = endOfDay(new Date(selectedYear, mIdx + 1, 0));
      }
    } else if (quarterMatch) {
      const startIdx = getMonthIndex(quarterMatch[2]);
      const endIdx = getMonthIndex(quarterMatch[3]);
      if (startIdx !== -1 && endIdx !== -1) {
        startDate = new Date(selectedYear, startIdx, 1);
        endDate = endOfDay(new Date(selectedYear, endIdx + 1, 0));
      }
    }

    if (!endDate) {
      endDate = endOfDay(new Date(selectedYear, 11, 31));
    }

    const rangeEnd = endDate;

    const parseLeadDate = (lead) => {
      const raw = lead?.LeadDate ?? lead?.leadDate;
      if (!raw) return null;
      const normalized = ValidDate(raw);
      if (normalized) {
        const [y, m, d] = normalized.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return isNaN(dt.getTime()) ? null : dt;
      }
      const dt = new Date(raw);
      return isNaN(dt.getTime()) ? null : dt;
    };

    const allYearLeads = baseLeads.filter((lead) => {
      const leadDate = parseLeadDate(lead);
      if (!leadDate) return false;
      return leadDate >= startDate && leadDate <= rangeEnd;
    });

    const monthMatchYTD = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)$/);
    const quarterMatchYTD = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)\s*-\s*([A-Za-z]+)$/);

    let ytdOnly;
    if (monthMatchYTD || quarterMatchYTD) {
      ytdOnly = allYearLeads;
    } else {
      const today = new Date();
      const yearMatch = selectedPeriod.match(/^(\d{4})/);
      const selYear = yearMatch ? Number(yearMatch[1]) : today.getFullYear();
      const toDateNum = (y, m, d) => y * 10000 + m * 100 + d;
      const ytdCutoffNum = toDateNum(selYear, today.getMonth() + 1, today.getDate());

      const getLeadDateNum = (raw) => {
        if (!raw) return null;
        const str = String(raw).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          const parts = str.split("-").map(Number);
          return toDateNum(parts[0], parts[1], parts[2]);
        }
        const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmyMatch) {
          const p1 = Number(dmyMatch[1]), p2 = Number(dmyMatch[2]), yr = Number(dmyMatch[3]);
          let day, month;
          if (p1 > 12) { day = p1; month = p2; }
          else if (p2 > 12) { month = p1; day = p2; }
          else { day = p1; month = p2; }
          return toDateNum(yr, month, day);
        }
        const parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
          return toDateNum(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
        }
        return null;
      };

      ytdOnly = allYearLeads.filter((lead) => {
        const num = getLeadDateNum(lead.LeadDate);
        return num != null && num <= ytdCutoffNum;
      });
    }

    const ytdLeads =
      getProjectCost && getRowKey
        ? ytdOnly.filter((lead) => getProjectCost(getRowKey(lead)) !== 0)
        : [];

    const parseNum = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;
    const parsePlExpenseStr = (s) => parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;

    let ytdTotalQV = 0;
    let ytdTotalC = 0;
    ytdLeads.forEach((r) => {
      const rk = getRowKey(r);
      ytdTotalQV += parseNum(r.quotedPrice ?? r.QuotedValue);
      ytdTotalC += parsePlExpenseStr(erpPlShowQuotedExpenseForRow(rk));
    });

    return {
      quotedValue: ytdTotalQV,
      productCost: ytdTotalC,
      grossProfit: ytdTotalQV - ytdTotalC,
    };
  }, [
    graphViewMode,
    isCrmPackage,
    grossProfitLeadReportsWithoutYears,
    selectedYears,
    quotationProjects,
    getRowKey,
    getProjectCost,
    erpPlShowQuotedExpenseForRow,
    erpPlValueSumForCard,
    erpPlCostSumForCard,
    erpPlGrossProfitSumForCard,
  ]);

  const leadsForTotalMetrics = useMemo(() => {
    const base = grossProfitLeadReportsWithoutYears || [];
    if (selectedYearForTotal == null) return [];
    const selectedPeriod = String(selectedYears?.[0] ?? "").trim();
    const isYearOnlySelection = /^\d{4}$/.test(selectedPeriod);

    // CRM requirement: Total & YTD should reflect the full selected year for year-only selections.
    // So we do NOT cap at today for CRM (future-dated leads must be included).
    // Non-CRM keeps the old behavior to avoid Total vs YTD drift.
    const shouldCapAtToday =
      !isCrmPackage &&
      isYearOnlySelection &&
      selectedYearForTotal === new Date().getFullYear();
    const today = new Date();
    const cutoffNum = shouldCapAtToday
      ? today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate()
      : null;

    const parseLeadYmd = (raw) => {
      if (!raw) return null;
      const str = String(raw).trim();
      const normalized = ValidDate(str);
      if (normalized) {
        const [y, m, d] = normalized.split("-").map(Number);
        if ([y, m, d].some((n) => !Number.isFinite(n))) return null;
        return { y, m, d };
      }
      // yyyy-mm-dd (treat as local date to avoid timezone shifting year/month/day)
      const iso = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\b|T)/);
      if (iso) return { y: Number(iso[1]), m: Number(iso[2]), d: Number(iso[3]) };
      // dd-mm-yyyy or dd/mm/yyyy or mm-dd-yyyy; disambiguate when possible
      const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
      if (dmy) {
        const p1 = Number(dmy[1]);
        const p2 = Number(dmy[2]);
        const y = Number(dmy[3]);
        let d, m;
        if (p1 > 12) { d = p1; m = p2; }
        else if (p2 > 12) { m = p1; d = p2; }
        else { m = p1; d = p2; }
        return { y, m, d };
      }
      // Fallback: Date() parsing
      const dt = new Date(str);
      if (!Number.isNaN(dt.getTime())) {
        return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
      }
      return null;
    };

    return base.filter((lead) => {
      const raw = lead?.LeadDate ?? lead?.leadDate;
      const ymd = parseLeadYmd(raw);
      if (!ymd) return false;
      const { y, m, d } = ymd;
      if (y !== selectedYearForTotal) return false;
      if (!shouldCapAtToday) return true;
      const leadNum = y * 10000 + m * 100 + d;
      return leadNum <= cutoffNum;
    });
  }, [grossProfitLeadReportsWithoutYears, selectedYearForTotal, selectedYears, isCrmPackage]);

  // ERP (Quoted): totals used by both Gross Profit and Net Margin cards must be consistent.
  // Compute them from the Gross Profit module's own filtered lead list (independent of P&L filters),
  // but using the P&L quoted-expense accessor for Cost (source of truth).
  const erpQuotedTotalsForCards = useMemo(() => {
    if (graphViewMode === "Actual" || isCrmPackage) return null;
    if (typeof erpPlShowQuotedExpenseForRow !== "function") return null;

    const rows = Array.isArray(leadsForTotalMetrics) ? leadsForTotalMetrics : [];
    // IMPORTANT: when there are no rows for the selected filters/year, return null so the UI shows "No data available"
    // instead of rendering 0-value graphs/tables.
    if (rows.length === 0) return null;

    const parseNum = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;
    const parsePlExpenseStr = (s) =>
      parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;

    let valueSum = 0;
    let costSum = 0;
    rows.forEach((r) => {
      const rk = getRowKey(r);
      valueSum += parseNum(r.quotedPrice ?? r.QuotedValue);
      costSum += parsePlExpenseStr(erpPlShowQuotedExpenseForRow(rk));
    });

    const grossProfitSum = valueSum - costSum;
    return { valueSum, costSum, grossProfitSum };
  }, [graphViewMode, isCrmPackage, leadsForTotalMetrics, getRowKey, erpPlShowQuotedExpenseForRow]);

  // CRM (Quoted): totals must come from Gross Profit module filters (not P&L filters).
  // Prefer the P&L "Quoted Expenses" accessor when available so Cost matches what the user entered in P&L,
  // without linking filter state between modules.
  const crmQuotedTotalsForCards = useMemo(() => {
    if (graphViewMode === "Actual" || !isCrmPackage) return null;
    const rows = Array.isArray(leadsForTotalMetrics) ? leadsForTotalMetrics : [];
    if (rows.length === 0) return null;

    const parseNum = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;
    const parsePlExpenseStr = (s) =>
      parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;
    const parseExpenseValue = (v) =>
      parseFloat(String(v || "0").replace(/[^0-9.-]/g, "")) || 0;

    const getQuotedValueForRow = (r) =>
      parseNum(
        r?.quotedPrice ??
          r?.QuotedValue ??
          r?.QuotedPrice ??
          r?.quotedvalue ??
          r?.quoted_price ??
          0
      );
    const getQuotedExpenseFromRowFields = (r) =>
      parseExpenseValue(
        r?.quotedExpense ??
          r?.QuotedExpense ??
          r?.quotedExpenses ??
          r?.QuotedExpenses ??
          r?.quoted_expense ??
          r?.Quoted_Expense ??
          0
      );

    let valueSum = 0;
    let costSum = 0;
    rows.forEach((r) => {
      const rk = getRowKey(r);
      valueSum += getQuotedValueForRow(r);
      // CRM requirement: Cost must equal the sum of the Quoted Expenses column values,
      // but Gross Profit filters must remain independent from P&L filters.
      //
      // In CRM, the displayed Quoted Expenses can come from persisted maps / backfills,
      // not always from raw lead fields. So when an accessor is provided (CRM path),
      // treat it as the source of truth for the per-row expense value.
      if (typeof erpPlShowQuotedExpenseForRow === "function") {
        costSum += parsePlExpenseStr(erpPlShowQuotedExpenseForRow(rk));
      } else {
        costSum += getQuotedExpenseFromRowFields(r);
      }
    });

    return { valueSum, costSum, grossProfitSum: valueSum - costSum };
  }, [graphViewMode, isCrmPackage, leadsForTotalMetrics, getRowKey, erpPlShowQuotedExpenseForRow]);

  const effectiveErpPlValueSumForCard =
    erpPlValueSumForCard != null && Number.isFinite(Number(erpPlValueSumForCard))
      ? Number(erpPlValueSumForCard)
      : (isCrmPackage ? crmQuotedTotalsForCards?.valueSum : erpQuotedTotalsForCards?.valueSum);
  const effectiveErpPlCostSumForCard =
    erpPlCostSumForCard != null && Number.isFinite(Number(erpPlCostSumForCard))
      ? Number(erpPlCostSumForCard)
      : (isCrmPackage ? crmQuotedTotalsForCards?.costSum : erpQuotedTotalsForCards?.costSum);
  const effectiveErpPlGrossProfitSumForCard =
    erpPlGrossProfitSumForCard != null && Number.isFinite(Number(erpPlGrossProfitSumForCard))
      ? Number(erpPlGrossProfitSumForCard)
      : (isCrmPackage ? crmQuotedTotalsForCards?.grossProfitSum : erpQuotedTotalsForCards?.grossProfitSum);

  // CRM YTD totals for Net Margin cards, aligned to the same lead-time window rules used by the CRM GP card.
  const crmYtdTotalsForNetMargin = useMemo(() => {
    if (graphViewMode === "Actual" || !isCrmPackage) return null;
    const baseLeads = Array.isArray(grossProfitLeadReportsWithoutYears)
      ? grossProfitLeadReportsWithoutYears
      : [];
    if (!selectedYears || selectedYears.length === 0) return null;

    const selectedPeriod = String(selectedYears?.[0] ?? "").trim();
    const yearOnlyMatch = selectedPeriod.match(/^(\d{4})$/);
    const monthMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)$/);
    const quarterMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)\s*-\s*([A-Za-z]+)$/);

    const selectedYear =
      yearOnlyMatch?.[1] ? Number(yearOnlyMatch[1]) :
        monthMatch?.[1] ? Number(monthMatch[1]) :
          quarterMatch?.[1] ? Number(quarterMatch[1]) :
            null;
    if (!selectedYear) return null;

    const getMonthIndex = (monthName) => {
      const months = {
        jan: 0, january: 0,
        feb: 1, february: 1,
        mar: 2, march: 2,
        apr: 3, april: 3,
        may: 4,
        jun: 5, june: 5,
        jul: 6, july: 6,
        aug: 7, august: 7,
        sep: 8, september: 8,
        oct: 9, october: 9,
        nov: 10, november: 10,
        dec: 11, december: 11,
      };
      const key = String(monthName || "").toLowerCase();
      return months[key] !== undefined ? months[key] : -1;
    };

    const endOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    let startDate = new Date(selectedYear, 0, 1);
    let endDate;
    if (monthMatch) {
      const mIdx = getMonthIndex(monthMatch[2]);
      if (mIdx !== -1) {
        startDate = new Date(selectedYear, mIdx, 1);
        endDate = endOfDay(new Date(selectedYear, mIdx + 1, 0));
      }
    } else if (quarterMatch) {
      const startIdx = getMonthIndex(quarterMatch[2]);
      const endIdx = getMonthIndex(quarterMatch[3]);
      if (startIdx !== -1 && endIdx !== -1) {
        startDate = new Date(selectedYear, startIdx, 1);
        endDate = endOfDay(new Date(selectedYear, endIdx + 1, 0));
      }
    }
    if (!endDate) endDate = endOfDay(new Date(selectedYear, 11, 31));

    // CRM Subscription Package requirement (Gross Profit section only):
    // For year-only selection of 2025, YTD cutoff date must be 8 May 2025 (inclusive).
    // YTD Net Margin must use the same YTD dataset as YTD Gross Profit.
    let rangeEnd = endDate;
    if (yearOnlyMatch && selectedYear === 2025) {
      rangeEnd = endOfDay(new Date(2025, 4, 8)); // 8 May 2025
    }

    const parseLeadDate = (lead) => {
      const raw = lead?.LeadDate ?? lead?.leadDate;
      if (!raw) return null;
      const normalized = ValidDate(raw);
      if (normalized) {
        const [y, m, d] = normalized.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return Number.isNaN(dt.getTime()) ? null : dt;
      }
      const dt = new Date(raw);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const rows = baseLeads.filter((lead) => {
      const dt = parseLeadDate(lead);
      if (!dt) return false;
      return dt >= startDate && dt <= rangeEnd;
    });
    if (rows.length === 0) return null;

    const parseNum = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;
    const parsePlExpenseStr = (s) =>
      parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;
    const parseExpenseValue = (v) =>
      parseFloat(String(v || "0").replace(/[^0-9.-]/g, "")) || 0;

    const getQuotedValueForRow = (r) =>
      parseNum(
        r?.quotedPrice ??
          r?.QuotedValue ??
          r?.QuotedPrice ??
          r?.quotedvalue ??
          r?.quoted_price ??
          0
      );
    const getQuotedExpenseFromRowFields = (r) =>
      parseExpenseValue(
        r?.quotedExpense ??
          r?.QuotedExpense ??
          r?.quotedExpenses ??
          r?.QuotedExpenses ??
          r?.quoted_expense ??
          r?.Quoted_Expense ??
          0
      );

    let valueSum = 0;
    let costSum = 0;
    rows.forEach((r) => {
      const rk = getRowKey(r);
      valueSum += getQuotedValueForRow(r);
      if (typeof erpPlShowQuotedExpenseForRow === "function") {
        costSum += parsePlExpenseStr(erpPlShowQuotedExpenseForRow(rk));
      } else {
        costSum += getQuotedExpenseFromRowFields(r);
      }
    });

    return { quotedValue: valueSum, productCost: costSum, grossProfit: valueSum - costSum };
  }, [graphViewMode, isCrmPackage, grossProfitLeadReportsWithoutYears, selectedYears, getRowKey, erpPlShowQuotedExpenseForRow]);

  return (

    <div>
      <div className="flex flex-col gap-1 bg-white rounded-md p-1 relative z-50">
        <div className="flex gap-2 text-sm font-medium items-center">
          <span className="text-xl font-semibold">Gross Profit</span>
          {canSeeQuotedTab && (
          <button
            onClick={() => setGraphViewMode("QuotedExpense")}
            className={`py-1 px-3 ${graphViewMode === "QuotedExpense" ? "bg-[#156082] text-white border-[#156082]" : "bg-gray-200 border-gray-300 hover:bg-gray-300"} border shadow-md rounded-lg transition-all duration-200`}
          >
            Quoted
          </button>
          )}
          {canSeeActualTab && (
          <button
            onClick={() => {
              setGraphViewMode("Actual");
            }}
            className={`py-1 px-3 ${graphViewMode === "Actual" ? "bg-[#156082] text-white border-[#156082]" : "bg-gray-200 border-gray-300 hover:bg-gray-300"} text-gray-800 border shadow-md rounded-lg transition-all duration-200`}
          >
            Actual
          </button>
          )}
        </div>
        <div className="flex justify-between gap-2">
        {canViewFilter && (
        <div className="flex">
          <div className="flex gap-3 p-1">
            <div
              className="relative flex gap-2 justify-center align-middle items-center"
              ref={dropdownRefGroup}
            >
              <label
                htmlFor="selectedStatus"
                className="block text-sm font-semibold text-gray-700 "
              >
                GROUP
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsGroupOpen(!isGroupOpen);
                }}
                className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
              >
                <span className="truncate min-w-0 flex-1 text-left">
                  {AssignedGroup === "All" ? "All" : (groupNameById.get(AssignedGroup) || "All")}
                </span>
                <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
              </button>
              {isGroupOpen && (
                <div className="absolute left-0 top-full w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                  <div
                    className="p-2 hover:bg-gray-200 cursor-pointer"
                    onClick={() => {
                      setAssignedGroup("All");
                      resetDependentFiltersForGroup();
                      setIsGroupOpen(false);
                      // setShowAllLeadTable(true);
                      // setCalculationTable(false);
                    }}
                  >
                    All
                  </div>
                  {GroupNamesL3.map((gItem) => {
                    const gid = String(gItem?._id ?? gItem?.id ?? "").trim();
                    if (!gid) return null;
                    const label =
                      gItem?.name ??
                      gItem?.newGroupName ??
                      gItem?.groupName ??
                      gItem?.GroupName ??
                      gid;
                    return (
                      <div
                        key={gid}
                        className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${AssignedGroup === gid ? "bg-gray-300" : ""
                          }`}
                        onClick={() => {
                          setAssignedGroup(gid);
                          resetDependentFiltersForGroup();
                          setIsGroupOpen(false);
                        }}
                      >
                        <label>{label}</label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 align-middle items-center scale-90" style={showClientDropdown ? { position: "relative", zIndex: 9999 } : undefined}>
            <h1 className="font-semibold">CLIENT</h1>
            <div className="relative" ref={dropdownRefclient}>
              <div className="flex  p-0 border rounded-md align-middle items-center">
                <ExpandMoreIcon
                  className="absolute right-1 cursor-pointer bg-white"
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                />

                <button
                  type="button"
                  onClick={() => setShowClientDropdown(!showClientDropdown)}
                  className=" text-left text-sm outline-none p-2 text-nowrap truncate w-40"
                >
                  {clientFilter ? clientFilter : <p>Select Client</p>}
                </button>
              </div>

              {showClientDropdown && (
                <div
                  className="absolute top-full left-0 w-full max-h-64 cursor-pointer overflow-y-auto bg-gray-100 text-sm border border-gray-400 bg-white shadow-md rounded-md z-50"
                  style={{ zIndex: 9999 }}
                >
                  <p
                    className="p-2 hover:bg-gray-200 "
                    onClick={() => {
                      handleClientChange("All");
                      // setShowAllLeadTable(true);
                      // setCalculationTable(false);
                    }}
                  >
                    All
                  </p>
                  {uniqueClients.map((clientName, idx) => (
                    <p
                      key={idx}
                      className="p-2 hover:bg-gray-200"
                      onClick={() => {
                        handleClientChange(clientName);
                        // setShowAllLeadTable(true);
                        // setCalculationTable(false);
                      }}
                    >
                      {clientName}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 p-1">
            <div
              className="relative flex gap-2 justify-center items-center"
              ref={dropdownRefYear}
            >
              <label className="block text-sm font-semibold text-gray-700">
                YEAR
              </label>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsYearOpen(!isYearOpen);
                }}
                className="w-24 relative outline-none date-picker-placeholder flex gap-1 justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
              >
                <span className="truncate min-w-0 flex-1 text-left">
                  {selectedYears?.length === 0
                    ? "All"
                    : selectedYears?.join(", ").length < 14 // Adjust length limit as needed
                      ? selectedYears?.join(", ")
                      : "Multiple"}
                </span>
                <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
              </button>

              {isYearOpen && (
                <div className="absolute left-0 top-full w-56 text-xs border overflow-hidden border-gray-400 bg-white shadow-md rounded-md z-50 max-h-64 overflow-y-auto">
                  {yearData?.map((item) => {
                    const isYearSelected =
                      (selectedYears?.length || 0) > 0 &&
                      selectedYears?.some((sel) => isSelectionInYear(sel, item.Year));

                    return (
                      <div
                        key={item.Year}
                        className="border-b border-gray-200"
                      >
                        <div className="flex align-middle items-center px-2">
                          <input
                            type="checkbox"
                            checked={isYearSelected}
                            onChange={(e) => {
                              setYearOnlySelection(item.Year);
                              setExpandedYear(isYearSelected ? null : item.Year);
                            }}
                          />{" "}
                          <button
                            className={`w-full text-left px-3 py-2 font-medium cursor-pointer`}
                            onClick={() => {
                              setExpandedYear(
                                expandedYear === item.Year ? null : item.Year
                              );
                            }}
                          >
                            {item.Year}
                          </button>
                        </div>

                        {expandedYear === item.Year && (
                          <div className="px-4 pb-3 text-gray-600">
                            <div className="text-[10px] font-semibold">
                              Months:
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1 text-[11px]">
                              {item.months.map((month) => {
                                const value = `${item.Year} ${month}`;
                                const isSelected =
                                  normalizeYearSelection(selectedYears?.[0]) === value;

                                return (
                                  <span
                                    key={month}
                                    onClick={() => handleYearSelection(value)}
                                    className={`px-2 py-[2px] rounded cursor-pointer border text-[11px]
                            ${isSelected
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100"
                                      }`}
                                  >
                                    {month}
                                  </span>
                                );
                              })}
                            </div>

                            <div className="mt-2 text-[10px] font-semibold">
                              Quarters:
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1 text-[11px]">
                              {item.quarter.map((qtr) => {
                                const value = `${item.Year} ${qtr}`;
                                const isSelected =
                                  normalizeYearSelection(selectedYears?.[0]) === value;

                                return (
                                  <span
                                    key={qtr}
                                    onClick={() => handleYearSelection(value)}
                                    className={`px-2 py-[2px] rounded cursor-pointer border text-[11px]
                            ${isSelected
                                        ? "bg-green-600 text-white border-green-600"
                                        : "bg-green-50 text-green-800 border-green-100 hover:bg-green-100"
                                      }`}
                                  >
                                    {qtr}
                                  </span>
                                );
                              })}

                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      <div className=" w-full flex gap-2 py-2  bg-[#f3f8f8]">
        {/* <div className="pt-2 flex gap-y-2 bg-[#f3f8f8]"> */}
        {/* {((!currentEmployee?.featureAccess) || (currentEmployee?.featureAccess?.includes("GROSS PROFIT") && (currentEmployee?.EmpAuthLevel !== "L1" || currentEmployee?.EmpAuthLevel !== "L0"))) && ( */}
        {canViewGrossProfitCards && (
            <div className="w-1/2 ">
              <SearchSalesGrossProfit
                key={`gp-card-${String(effectiveErpPlValueSumForCard ?? "")}-${String(effectiveErpPlCostSumForCard ?? "")}-${String(effectiveErpPlGrossProfitSumForCard ?? "")}-${graphViewMode}`}
                selectedYears={selectedYears}
                perdiem={perdiem}
                averagecost={averagecost}
                quotationProjects={quotationProjects}
                actualQuotationProjects={gpActualFinanceDataProject}
                calculatedProjects={calculatedProjects}
                packages={profiles?.[0]?.subscriptionpackage}
                showTable={graphViewMode}
                searchedLeadReports={leadsForTotalMetrics}
                searchedLeadReportswithoutyears={grossProfitLeadReportsWithoutYears}
                getProjectCost={getProjectCost}
                getActualProjectCost={getActualProjectCost}
                getRowKey={getRowKey}
                canViewTotalGrossProfitGraph={canViewTotalGrossProfitGraph}
                canViewTotalGrossProfitTable={canViewTotalGrossProfitTable}
                canViewYtdGrossProfitGraph={canViewYtdGrossProfitGraph}
                canViewYtdGrossProfitTable={canViewYtdGrossProfitTable}
                filterVersion={filterVersion}
                erpPlQuotedTotalsForGrossProfitCard={
                  erpPlQuotedTotalsForGrossProfitCard
                }
                erpPlValueSumForCard={effectiveErpPlValueSumForCard}
                erpPlCostSumForCard={effectiveErpPlCostSumForCard}
                erpPlGrossProfitSumForCard={effectiveErpPlGrossProfitSumForCard}
                erpPlShowQuotedExpenseForRow={erpPlShowQuotedExpenseForRow}
                plGetQuotedValueForRow={plGetQuotedValueForRow}
                plGetQuotedExpenseForRow={plGetQuotedExpenseForRow}
              />
            </div>
          )}
        {
          // ((!currentEmployee?.featureAccess) || (currentEmployee?.featureAccess?.includes("NET MARGIN") && (currentEmployee?.EmpAuthLevel !== "L1" || currentEmployee?.EmpAuthLevel !== "L0"))) && (
          canViewNetMarginCards && (
            <div className="w-1/2 ">
              <SearchedSalesNetMargin
                selectedYears={selectedYears}
                perdiem={perdiem}
                averagecost={averagecost}
                quotationProjects={quotationProjects}
                actualQuotationProjects={gpActualFinanceDataProject}
                calculatedProjects={calculatedProjects}
                packages={profiles?.[0]?.subscriptionpackage}
                showTable={graphViewMode}
                searchedLeadReports={leadsForTotalMetrics}
                searchedLeadReportswithoutyears={grossProfitLeadReportsWithoutYears}
                getProjectCost={getProjectCost}
                getActualProjectCost={getActualProjectCost}
                getRowKey={getRowKey}
                currentEmployee={currentEmployee}
                canViewNetMarginGraph={canViewNetMarginGraph}
                canViewNetMarginTable={canViewNetMarginTable}
                canViewYtdNetMarginGraph={canViewYtdNetMarginGraph}
                canViewYtdNetMarginTable={canViewYtdNetMarginTable}
                filterVersion={filterVersion}
                erpPlGrossProfitSumForCard={effectiveErpPlGrossProfitSumForCard}
                erpPlValueSumForCard={effectiveErpPlValueSumForCard}
                erpPlCostSumForCard={effectiveErpPlCostSumForCard}
                erpPlYtdGrossProfitForNetMargin={(isCrmPackage ? crmYtdTotalsForNetMargin?.grossProfit : erpYtdTotalsForNetMargin?.grossProfit)}
                erpPlYtdQuotedValueForNetMargin={(isCrmPackage ? crmYtdTotalsForNetMargin?.quotedValue : erpYtdTotalsForNetMargin?.quotedValue)}
                erpPlYtdProductCostForNetMargin={(isCrmPackage ? crmYtdTotalsForNetMargin?.productCost : erpYtdTotalsForNetMargin?.productCost)}
                plGetQuotedValueForRow={plGetQuotedValueForRow}
                plGetQuotedExpenseForRow={plGetQuotedExpenseForRow}
              />
            </div>
          )}
      </div>
    </div>
    </div>
  )
}

export default FinanceGraph
